import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  FormHelperText,
} from "@mui/material";
import { X, MapPin } from "lucide-react";
import {
  normalizeMapConfig,
  buildSectionMapPatch,
  addWaypoint,
  updateWaypoint,
  removeWaypoint,
  reorderWaypoints,
} from "./mapWaypoints";
import { useScrollytellTranslation } from "../Admin/scrollytellContext";
import { HistoricMapOverlayControls } from "./HistoricMapOverlayControls";
import { WaypointsListControls } from "./WaypointsListControls";

const getRouteStyleHelp = (style, t) => {
  switch (style) {
    case "sea":
      return t(
        "mapConfigSubMenu.routeStyleHelp.sea",
        "Passer for kystruter, skipsfart og sjøreiser der kystlinje og sjø holdes i sikte med rolig bevegelse."
      );
    case "trek":
      return t(
        "mapConfigSubMenu.routeStyleHelp.trek",
        "Passer for landreiser, vandring og ekspedisjoner i daler og fjell der kameraet følger terrenget tett."
      );
    case "direct":
      return t(
        "mapConfigSubMenu.routeStyleHelp.direct",
        "Passer for rette linjer uten bue eller dynamisk zooming."
      );
    case "flight":
    default:
      return t(
        "mapConfigSubMenu.routeStyleHelp.flight",
        "Passer for lange reiser over landegrenser og hav der man trenger stor oversikt."
      );
  }
};

const SelectField = ({ id, label, value, onChange, options, helperText }) => (
  <FormControl fullWidth size="small">
    <InputLabel id={id}>{label}</InputLabel>
    <Select native labelId={id} label={label} inputProps={{ "aria-label": label }} value={value} onChange={onChange}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </Select>
    {helperText && <FormHelperText sx={{ fontSize: "11px", mt: 0.5 }}>{helperText}</FormHelperText>}
  </FormControl>
);

export const MapConfigSubMenu = ({ mapConfig = {}, onUpdate = () => {}, onClose = () => {} }) => {
  const t = useScrollytellTranslation();
  const currentConfig = normalizeMapConfig(mapConfig);

  const emitUpdate = (cfg) => onUpdate(buildSectionMapPatch(cfg));
  const handleFieldChange = (field, val) => emitUpdate({ ...currentConfig, [field]: val });
  const handleToggle = (field, val) => emitUpdate({ ...currentConfig, [field]: typeof val === "boolean" ? val : !currentConfig[field] });
  const handleWaypointTitle = (idx, title) => emitUpdate(updateWaypoint(currentConfig, idx, { title }));
  const handleRemove = (idx) => emitUpdate(removeWaypoint(currentConfig, idx));
  const handleReorder = (fromIdx, toIdx) => emitUpdate(reorderWaypoints(currentConfig, fromIdx, toIdx));
  const handleAdd = (place) => emitUpdate(addWaypoint(currentConfig, {
    coordinate: place.coordinate,
    title: place.title || place.display_name || t("mapConfigSubMenu.defaultNewWaypointTitle", "Nytt stoppested"),
  }));
  const handleUpdateOverlay = (historicOverlay) => {
    let nextWaypoints = currentConfig.waypoints;
    if (historicOverlay?.enabled && Array.isArray(nextWaypoints) && nextWaypoints.length > 0) {
      const needsInit = nextWaypoints.some((w) => w.historicMapOpacity === undefined);
      if (needsInit) {
        nextWaypoints = nextWaypoints.map((w, i) => ({
          ...w,
          historicMapOpacity: w.historicMapOpacity !== undefined ? w.historicMapOpacity : (i === 0 ? 1.0 : 0.0),
        }));
      }
    }
    emitUpdate({ ...currentConfig, historicOverlay, waypoints: nextWaypoints });
  };
  const handleWaypointOpacity = (idx, opacity) => emitUpdate(updateWaypoint(currentConfig, idx, { historicMapOpacity: opacity }));

  const containerRef = useRef(null);
  const [maxMenuHeight, setMaxMenuHeight] = useState("calc(100vh - 280px)");

  const setContainerRef = useCallback((node) => {
    containerRef.current = node;
    if (node && typeof window !== "undefined") {
      const rect = node.getBoundingClientRect();
      if (rect.top > 0 && window.innerHeight > 0) {
        setMaxMenuHeight(`${Math.max(200, Math.floor(window.innerHeight - rect.top - 16))}px`);
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateH = () => {
      const node = containerRef.current;
      if (node && node.getBoundingClientRect().top > 0 && window.innerHeight > 0) {
        setMaxMenuHeight(`${Math.max(200, Math.floor(window.innerHeight - node.getBoundingClientRect().top - 16))}px`);
      }
    };
    window.addEventListener("resize", updateH);
    window.addEventListener("scroll", updateH, true);
    return () => {
      window.removeEventListener("resize", updateH);
      window.removeEventListener("scroll", updateH, true);
    };
  }, []);

  const themeOptions = [
    { value: "osm", label: "OpenStreetMap" },
    { value: "nordic-dark", label: "Nordic Dark (Nattkart)" },
    { value: "antique-parchment", label: "Historisk pergament" },
    { value: "satellite", label: "Satellitt (Esri)" },
    { value: "minimal-positron", label: "Minimal Positron (Lys)" },
  ];

  const routeOptions = [
    { value: "flight", label: "Fly / Luftlinje" },
    { value: "sea", label: "Maritim / Sjøvei" },
    { value: "trek", label: "Ekspedisjon / Vandring" },
    { value: "direct", label: "Direktelinje" },
  ];

  const distanceOptions = [
    { value: "km", label: "Kilometer (km)" },
    { value: "nm", label: "Nautiske mil (nm)" },
  ];

  return (
    <Box
      ref={setContainerRef}
      data-testid="map-config-submenu-container"
      style={{ maxHeight: maxMenuHeight, overflowY: "auto", overscrollBehavior: "contain", boxSizing: "border-box" }}
      sx={{
        width: 320, backgroundColor: "#FFFFFF", color: "#222222", p: 2, pb: 2.5, borderRadius: "4px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.15)", border: "1px solid #E0E0E0", display: "flex", flexDirection: "column", gap: 2,
        "& > *": { flexShrink: 0 },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MapPin size={18} color="#0284C7" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#111827" }}>
            {t("mapConfigSubMenu.title", "Kartinnstillinger")}
          </Typography>
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label={t("mapConfigSubMenu.close", "Lukk kartmeny")}>
            <X size={18} />
          </IconButton>
        )}
      </Box>

      <SelectField id="map-theme-label" label={t("mapConfigSubMenu.themeLabel", "Karttema")} value={currentConfig.theme || "osm"} onChange={(e) => handleFieldChange("theme", e.target.value)} options={themeOptions} />
      <SelectField id="route-style-label" label={t("mapConfigSubMenu.routeStyleLabel", "Reisemodus")} value={currentConfig.routeStyle || "flight"} onChange={(e) => handleFieldChange("routeStyle", e.target.value)} options={routeOptions} helperText={getRouteStyleHelp(currentConfig.routeStyle || "flight", t)} />
      <SelectField id="distance-unit-label" label={t("mapConfigSubMenu.distanceUnitLabel", "Avstandsenhet")} value={currentConfig.distanceUnit || "km"} onChange={(e) => handleFieldChange("distanceUnit", e.target.value)} options={distanceOptions} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <FormControlLabel control={<Switch size="small" checked={Boolean(currentConfig.vignette)} onChange={(e) => handleToggle("vignette", e.target.checked)} inputProps={{ "aria-label": t("mapConfigSubMenu.vignette", "Vinjett") }} />} label={<Typography sx={{ fontSize: "13px" }}>{t("mapConfigSubMenu.vignette", "Vinjett")}</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={Boolean(currentConfig.compassRose)} onChange={(e) => handleToggle("compassRose", e.target.checked)} inputProps={{ "aria-label": t("mapConfigSubMenu.compassRose", "Kompassrose") }} />} label={<Typography sx={{ fontSize: "13px" }}>{t("mapConfigSubMenu.compassRose", "Kompassrose")}</Typography>} />
        <FormControlLabel control={<Switch size="small" checked={Boolean(currentConfig.showHud)} onChange={(e) => handleToggle("showHud", e.target.checked)} inputProps={{ "aria-label": t("mapConfigSubMenu.expeditionHud", "Ekspedisjons-HUD") }} />} label={<Typography sx={{ fontSize: "13px" }}>{t("mapConfigSubMenu.expeditionHud", "Ekspedisjons-HUD")}</Typography>} />
      </Box>

      <Divider sx={{ borderColor: "#E2E8F0" }} />
      <HistoricMapOverlayControls
        historicOverlay={currentConfig.historicOverlay}
        onUpdateOverlay={handleUpdateOverlay}
        waypoints={currentConfig.waypoints}
        onUpdateWaypointOpacity={handleWaypointOpacity}
      />
      <Divider sx={{ borderColor: "#E2E8F0" }} />
      <WaypointsListControls
        waypoints={currentConfig.waypoints}
        onUpdateTitle={handleWaypointTitle}
        onRemove={handleRemove}
        onAdd={handleAdd}
        onReorder={handleReorder}
      />
    </Box>
  );
};
